import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { HaveABusinessHook } from "@/components/HaveABusinessHook";

function audienceBlockFromError(e) {
  const d = e?.response?.data?.detail;
  if (e?.response?.status === 403 && d && typeof d === "object" && d.code === "audience_blocked") {
    return d;
  }
  return null;
}

export function ReferralResponseSection({ code, referrerName, businessName, initialResponse }) {
  const { user } = useAuth();
  const [response, setResponse] = useState(initialResponse || null);
  const [showNeedBox, setShowNeedBox] = useState(false);
  const [needText, setNeedText] = useState("");
  const [busy, setBusy] = useState(false);
  const [block, setBlock] = useState(null);

  const submit = async (type, message = null) => {
    setBusy(true);
    setBlock(null);
    try {
      const { data } = await api.post(`/r/${code}/respond`, {
        type,
        message,
        respondent_name: user?.name || null,
        respondent_phone: user?.phone || null,
      });
      setResponse(data);
      setShowNeedBox(false);
    } catch (e) {
      const blocked = audienceBlockFromError(e);
      if (blocked) setBlock(blocked);
      else toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (block) {
    return (
      <div
        className="mt-8 rounded-[14px] border border-border bg-card px-5 py-5"
        data-testid="audience-block-screen"
      >
        <p className="text-lg font-bold" data-testid="audience-block-title">{block.title}</p>
        <p className="mt-2 text-sm text-muted-foreground" data-testid="audience-block-message">
          {block.message}
        </p>
        <Button asChild className="mt-4 bg-brand-green hover:bg-brand-green-dark" data-testid="audience-block-browse">
          <Link to="/">Browse businesses</Link>
        </Button>
      </div>
    );
  }

  if (response) {
    return (
      <div
        className="mt-8 rounded-[14px] border border-brand-green/20 bg-brand-green/5 px-5 py-4 text-sm text-brand-green"
        data-testid="referral-response-confirmation"
      >
        <p>{response.confirmation}</p>
        <HaveABusinessHook className="mt-3 text-brand-green/80" testID="have-a-business-after-response" />
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="referral-response-section">
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-brand-green">
        Let {referrerName} know
      </p>
      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          className="h-auto justify-start whitespace-normal px-4 py-3 text-left"
          disabled={busy}
          onClick={() => submit("interested")}
          data-testid="response-interested-btn"
        >
          Thanks — I&apos;ll reach out to {businessName}
        </Button>
        <Button
          variant="outline"
          className="h-auto justify-start whitespace-normal px-4 py-3 text-left"
          disabled={busy}
          onClick={() => submit("not_a_fit")}
          data-testid="response-not-a-fit-btn"
        >
          Thanks, but this isn&apos;t what I&apos;m looking for
        </Button>
        {!showNeedBox ? (
          <Button
            variant="outline"
            className="h-auto justify-start whitespace-normal px-4 py-3 text-left"
            disabled={busy}
            onClick={() => setShowNeedBox(true)}
            data-testid="response-need-else-btn"
          >
            Not this — here&apos;s what I actually need…
          </Button>
        ) : (
          <div className="rounded-[14px] border border-border bg-card p-4" data-testid="need-else-box">
            <Textarea
              value={needText}
              onChange={(e) => setNeedText(e.target.value)}
              placeholder="What are you looking for?"
              className="min-h-[88px]"
              data-testid="need-else-input"
            />
            <Button
              className="mt-3 bg-brand-green hover:bg-brand-green-dark"
              disabled={busy || !needText.trim()}
              onClick={() => submit("need_something_else", needText.trim())}
              data-testid="send-to-referrer-btn"
            >
              {busy ? "Sending…" : `Send to ${referrerName}`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
